import type { OrgType } from './persona'

export type MosaicPhoto = {
  id: string
  storagePath: string
  thumbnailUrl?: string
  cloudStorageUrl?: string
  isCoverPhoto: boolean
  caption?: string
  uploadStatus?: 'uploading' | 'error'
  errorMessage?: string
  mediaType?: 'photo' | 'video'
  durationSeconds?: number
  width?: number
  height?: number
  readOnly?: boolean
  editable?: boolean
}

function normalizeStorageHost(url: string, supabaseUrl: string): string {
  try {
    const parsed = new URL(url)
    const target = new URL(supabaseUrl)
    if (parsed.host !== target.host) {
      parsed.protocol = target.protocol
      parsed.host = target.host
      return parsed.toString()
    }
  } catch {
    return url
  }
  return url
}

export function resolvePhotoUrl(
  photo: Pick<MosaicPhoto, 'thumbnailUrl' | 'cloudStorageUrl'>,
  supabaseUrl: string,
): string | undefined {
  const url = photo.thumbnailUrl ?? photo.cloudStorageUrl
  if (!url) return undefined
  if (url.startsWith('blob:') || url.startsWith('data:')) return url
  if (url.startsWith('http')) return normalizeStorageHost(url, supabaseUrl)
  return undefined
}

export function resolveStorageUrl(
  url: string | null | undefined,
  supabaseUrl: string,
): string | undefined {
  if (!url) return undefined
  if (url.startsWith('blob:') || url.startsWith('data:')) return url
  if (url.startsWith('http')) return normalizeStorageHost(url, supabaseUrl)
  return undefined
}

export type GalleryPhoto = {
  id: string
  filename: string
  huntId?: string
  dogId?: string
  orgId?: string
  orgType?: OrgType
  userId: string
  filesize: number
  mimetype: string
  storagePath: string
  thumbnailPath?: string
  cloudStorageUrl?: string
  thumbnailUrl?: string
  caption?: string
  takenAt?: string
  uploadedAt: string
  createdAt: string
  updatedAt: string
  sourceType?: string
  mediaType?: 'photo' | 'video'
  durationSeconds?: number
  width?: number
  height?: number
  album?: string
}

export type PhotoWithHuntInfo = {
  id: string
  storagePath: string
  thumbnailPath?: string
  cloudStorageUrl?: string
  thumbnailUrl?: string
  isCoverPhoto?: boolean
  caption?: string
  takenAt: string
  filesize: number
  mimetype: string
  huntDate?: string
  huntTitle?: string
  huntStartTime?: string
  huntType?: string
  blindName?: string
  huntId?: string
  userId: string
}

export type HuntPhotoGroupOutput = {
  huntId: string
  huntName: string
  startTime?: string
  blindName?: string
  photos: PhotoWithHuntInfo[]
}

export type DayHuntGroup = {
  date: string
  hunts: HuntPhotoGroupOutput[]
}

export type GroupedHuntPhotosResult = {
  groups: DayHuntGroup[]
  totalPhotos: number
  totalHunts: number
}

export type DogPhoto = {
  id: string
  filename: string
  storagePath: string
  thumbnailPath: string | null
  cloudStorageUrl: string | null
  thumbnailUrl: string | null
  caption: string | null
  dogId: string | null
  photoType: string
  takenAt: string | null
  filesize: number
  mimetype: string
  createdAt: string
  userId: string
  sourceType?: 'gallery_upload' | 'dog_photo'
  album?: 'dogs'
  mediaType?: 'photo' | 'video'
  durationSeconds?: number
  dogPhotoKind?: 'profile'
}
